import { clearCurrentUser, fetchCurrentUser, sendSingleSMS } from './user.duck';
import {
  callToNotifyNewProvider,
  createUserWithIdp,
  loginWithTurnstile,
  signupWithTurnstile,
} from '../util/api';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { isNotEmptyString } from '../util/string';
import { trackSignUp } from '../analytics/googleTagManager';

const { REACT_APP_MARKETPLACE_NAME: APP_NAME } = process.env;

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        isLoggedInAs: loggedInAs(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return {
        ...state,
        logoutInProgress: false,
        isAuthenticated: false,
        isLoggedInAs: false,
        authScopes: [],
      };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

const loginDirectly = (dispatch, sdk, username, password, groupParams = {}, logEvent = false) => {
  return sdk
    .login({ username, password })
    .then(() => {
      dispatch(loginSuccess());
      return dispatch(fetchCurrentUser(null, groupParams, logEvent, true));
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(loginError(error));
      return { error };
    });
};

const buildTechnicalBuyerSignupParams = (params = {}, businessAddress = null) => {
  const {
    email,
    password,
    phone,
    phoneNumber,
    publicData,
    protectedData,
    turnstileToken,
    ...rest
  } = params;

  const resolvedPhone =
    phone || phoneNumber || publicData?.phone || protectedData?.phoneNumber || undefined;

  return {
    email,
    password,
    ...rest,
    publicData: {
      ...(publicData || {}),
      ...(businessAddress || {}),
      ...(resolvedPhone ? { phone: resolvedPhone } : {}),
    },
    protectedData: {
      ...(protectedData || {}),
      ...(resolvedPhone ? { phoneNumber: resolvedPhone } : {}),
    },
  };
};

const runSignupSuccessEffects = (dispatch, userParams, signupMethod) => {
  const userType = userParams.publicData?.userType || 'buyer';

  plausible(userType == 'buyer' ? 'signUpBuyer' : 'signUpSeller', {
    props: { email: userParams.email },
  });

  trackSignUp({ method: signupMethod, userType });

  if (userParams.publicData?.phone) {
    dispatch(
      sendSingleSMS(
        userParams.publicData.phone,
        `Welcome to ${APP_NAME}! We're excited to have you with us. Thanks for joining!`
      )
    );
  }

  dispatch(signupSuccess());
};

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password, groupParams = {}, logEvent = false) => (
  dispatch,
  getState,
  sdk
) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }

  dispatch(loginRequest());

  // Turnstile token is injected into form values by the LoginForm.
  // If it comes through as a form parameter, use the server proxy.
  // The SDK is used for token exchange (authInfo) after a successful
  // response sets the cookie.
  const { turnstileToken, ...rest } = groupParams;

  return loginWithTurnstile({
    username,
    password,
    'cf-turnstile-response': turnstileToken,
  })
    .then(() => {
      dispatch(loginSuccess());
      // Use forceRefetch = true to bypass isAuthenticated check in fetchCurrentUser
      return dispatch(fetchCurrentUser(null, rest, logEvent, true));
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(loginError(error));
      return { error };
    });
};

export const loginWithoutTurnstile = (username, password, groupParams = {}, logEvent = false) => (
  dispatch,
  getState,
  sdk
) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }

  dispatch(loginRequest());

  return loginDirectly(dispatch, sdk, username, password, groupParams, logEvent);
};

export const loginAsGuest = (username, password, groupParams = {}, logEvent = false) =>
  loginWithoutTurnstile(username, password, groupParams, logEvent);

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = (params, groupParams, businessAddress) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  // Note: params are already structured on AuthenticationPage (handleSubmitSignup)

  // Turnstile token is injected into form values by the SignupForm.
  const { turnstileToken, ...userParamsRest } = params;

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically (the server proxy handles auto-login).
  let { publicData } = userParamsRest;
  userParamsRest.publicData = { ...publicData, ...businessAddress };
  if (
    groupParams?.ownerId &&
    isNotEmptyString(groupParams.ownerId) &&
    groupParams?.invitationId &&
    isNotEmptyString(groupParams.invitationId)
  ) {
    userParamsRest.publicData.userType = 'provider';
    userParamsRest.publicData.sellerType = 'associate';
  }

  return signupWithTurnstile({
    ...userParamsRest,
    'cf-turnstile-response': turnstileToken,
  })
    .then(() => {
      runSignupSuccessEffects(dispatch, userParamsRest, 'email');
    })
    .then(() => {
      // After signup, fetch the current user (the proxy already logged in
      // and set the cookie, so fetchCurrentUser should work).
      return dispatch(fetchCurrentUser(null, groupParams, false, true));
    })
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: userParamsRest.email,
        firstName: userParamsRest.firstName,
        lastName: userParamsRest.lastName,
      });
    });
};

export const signupFromGuestCheckout = (
  params,
  businessAddress = null,
  signupMethod = 'guest_checkout'
) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }

  dispatch(signupRequest());

  const technicalSignupParams = buildTechnicalBuyerSignupParams(params, businessAddress);
  const { email, password, ...createParams } = technicalSignupParams;

  return sdk.currentUser
    .create({ email, password, ...createParams })
    .then(() => {
      runSignupSuccessEffects(dispatch, technicalSignupParams, signupMethod);
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(signupError(error));
      log.error(e, 'technical-signup-failed', {
        email,
        firstName: technicalSignupParams.firstName,
        lastName: technicalSignupParams.lastName,
      });
      return { error };
    });
};

export const sendNotificationNewProvider = sellerData => (dispatch, getState, sdk) => {
  callToNotifyNewProvider(sellerData)
    .then(() => {})
    .catch(() => {});
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(res => {
      trackSignUp({ method: 'identity_provider' });
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};
